<template>
  <div class="category-services-page">
    <v-container class="py-5">
      <div class="page-header mb-5">
        <v-btn icon class="back-btn" @click="$router.back()">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <div class="eyebrow">Category</div>
          <h1 class="title">{{ categoryName }}</h1>
        </div>
      </div>

      <div v-if="loading" class="loading-wrap">
        <div class="text-center">
          <v-progress-circular indeterminate color="#f46523" size="54" />
          <p class="mt-3 grey--text">Loading services...</p>
        </div>
      </div>

      <v-row v-else-if="locationRequired" justify="center" class="py-12">
        <v-col cols="12" md="8" class="text-center">
          <v-icon size="64" color="#f46523">mdi-map-marker-alert-outline</v-icon>
          <h3 class="mt-4">Location permission required</h3>
          <p class="grey--text mt-2">
            {{ locationMessage }}
          </p>
          <v-btn
            color="#f46523"
            dark
            class="mt-4"
            :loading="loading"
            :disabled="loading"
            @click="handleEnableLocationClick"
          >
            Enable Location
          </v-btn>
          <div class="location-how-to-box">
            <h4 class="location-how-to-title">
              <v-icon small class="mr-1">mdi-information-outline</v-icon>
              How to enable location (if the browser prompt doesn’t appear)
            </h4>
            <div class="location-how-to-steps">
              <p class="location-how-to-browser mb-1"><strong>Chrome / Edge</strong></p>
              <ol>
                <li>Click the <strong>lock</strong> or <strong>info</strong> icon left of the address bar → <strong>Site settings</strong> → <strong>Location</strong> → <strong>Allow</strong>.</li>
                <li>Refresh the page or click Enable Location again.</li>
              </ol>
              <p class="location-how-to-browser mb-1 mt-3"><strong>Safari</strong></p>
              <ol>
                <li>Settings → Safari → Location (or Settings for This Website → Location) → <strong>Allow</strong> or <strong>Ask</strong>, then reload.</li>
              </ol>
            </div>
            <div class="location-guide-links mt-3">
              <span class="location-guide-links-label">Open browser guide:</span>
              <a href="https://support.google.com/chrome/answer/114662" target="_blank" rel="noopener noreferrer" class="location-guide-link">Chrome / Edge</a>
              <span class="location-guide-sep">·</span>
              <a href="https://support.apple.com/guide/safari/websites-ibrwe2159985/mac" target="_blank" rel="noopener noreferrer" class="location-guide-link">Safari</a>
            </div>
            <p class="location-tip mt-3 mb-0">
              If the prompt still doesn’t appear (e.g. you blocked it before), allow location in browser settings above, or open this page in a <strong>private / incognito window</strong> and the prompt will show again.
            </p>
          </div>
        </v-col>
      </v-row>

      <v-row v-else-if="services.length" class="services-grid" justify="center">
        <v-col
          v-for="service in services"
          :key="service._id"
          cols="12"
          sm="6"
          md="4"
          lg="3"
        >
          <v-card class="service-card" outlined @click="openService(service)">
            <v-img
              :src="service.image?.[0] || placeholderImage"
              height="180"
              cover
            />
            <v-card-text class="pa-4 text-center">
              <h3 class="service-name">{{ service.title }}</h3>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-else justify="center" class="py-12">
        <v-col cols="12" md="8" class="text-center">
          <v-icon size="64" color="#f46523" class="opacity-30">
            mdi-alert-circle-outline
          </v-icon>
          <h3 class="mt-4">{{ !resolvedCategoryId && categorySlug ? 'Category not found' : 'No services available' }}</h3>
          <p class="grey--text mt-2">
            {{ !resolvedCategoryId && categorySlug ? 'We couldn\'t find this category.' : 'We couldn\'t find services for this category right now.' }}
          </p>
          <v-btn color="#f46523" text class="mt-3" @click="$router.back()">
            Back to Categories
          </v-btn>
        </v-col>
      </v-row>

    </v-container>
  </div>
</template>

<script>
import { fetchServicesByCategory, fetchCategories } from "@/services/api";
import { slugify, slugifyForUrl, slugToMatch } from "@/utils/slug";
import { enrichAcServiceContent } from "@/utils/acServiceContent";

export default {
  name: "CategoryServicesView",
  data() {
    return {
      services: [],
      loading: true,
      locationRequired: false,
      locationMessage:
        "Please allow location access to view available services in this category.",
      placeholderImage: "https://via.placeholder.com/600x400?text=Service+Image",
      resolvedCategoryId: null,
      resolvedCategoryName: null,
    };
  },
  computed: {
    categorySlug() {
      const slug = this.$route.params.categorySlug;
      return slug ? decodeURIComponent(slug).toLowerCase() : "";
    },
    categoryName() {
      if (this.resolvedCategoryName) return this.resolvedCategoryName;
      const fromSession = sessionStorage.getItem("selectedCategory");
      if (fromSession) {
        try {
          const parsed = JSON.parse(fromSession);
          if (slugify(parsed?.name || "") === slugToMatch(this.categorySlug)) {
            return parsed?.name || "Services";
          }
        } catch (error) {
          /* ignore */
        }
      }
      return this.categorySlug ? this.formatSlugForDisplay(this.categorySlug) : "Services";
    },
  },
  watch: {
    categorySlug() {
      this.resolvedCategoryId = null;
      this.resolvedCategoryName = null;
      this.initWithLocation();
    },
  },
  mounted() {
    this.initWithLocation();
  },
  methods: {
    getKochiFallbackLocation() {
      const location = {
        latitude: 9.9312,
        longitude: 76.2673,
        allowed: true,
        error: null,
        isFallback: true,
      };
      try {
        localStorage.setItem("userLocation", JSON.stringify(location));
        sessionStorage.setItem("userLocation", JSON.stringify(location));
      } catch (e) {
        /* ignore */
      }
      return location;
    },
    getLocationFromStorage() {
      try {
        const raw =
          localStorage.getItem("userLocation") ||
          sessionStorage.getItem("userLocation");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed?.latitude == null || parsed?.longitude == null) return null;
        return parsed;
      } catch (e) {
        return null;
      }
    },
    async initWithLocation() {
      if (
        typeof window !== "undefined" &&
        !window.isSecureContext &&
        window.location.hostname !== "localhost"
      ) {
        this.locationRequired = true;
        this.locationMessage =
          "Location requires a secure connection (HTTPS). Please open this site on HTTPS and retry.";
        this.loading = false;
        return;
      }
      if (!navigator.geolocation) {
        const fallback = this.getKochiFallbackLocation();
        this.locationRequired = false;
        this.locationMessage =
          "Please allow location access to view available services in this category.";
        this.loading = true;
        await this.resolveCategoryId();
        if (!this.resolvedCategoryId) {
          this.services = [];
          this.loading = false;
          return;
        }
        await this.loadServices(fallback);
        return;
      }
      // Request location as soon as they enter the page so the browser modal can show.
      // Small delay so the page is interactive first (can help the prompt appear on some devices).
      this.locationMessage = "Requesting location permission...";
      this.loading = true;
      setTimeout(() => this.requestLocationAndLoad(), 150);
    },
    formatSlugForDisplay(slug) {
      if (!slug) return "Services";
      return slug
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    },

    async resolveCategoryId() {
      return new Promise((resolve) => {
        const slug = this.categorySlug;
        if (!slug) {
          this.resolvedCategoryName = null;
          resolve(null);
          return;
        }
        const fromSession = sessionStorage.getItem("selectedCategory");
        if (fromSession) {
          try {
            const parsed = JSON.parse(fromSession);
            if (slugify(parsed?.name || "") === slugToMatch(slug) && parsed?._id) {
              this.resolvedCategoryId = parsed._id;
              this.resolvedCategoryName = parsed?.name || null;
              resolve(parsed._id);
              return;
            }
          } catch (e) {
            /* ignore */
          }
        }
        fetchCategories({})
          .then((res) => {
            const list = Array.isArray(res?.data)
              ? res.data
              : res?.data?.data ?? [];
            const arr = Array.isArray(list) ? list : [];
            const found = arr.find((c) => slugify(c.name || "") === slugToMatch(slug));
            if (found?._id) {
              this.resolvedCategoryId = found._id;
              this.resolvedCategoryName = found.name || null;
              resolve(found._id);
            } else {
              this.resolvedCategoryId = null;
              this.resolvedCategoryName = null;
              resolve(null);
            }
          })
          .catch(() => {
            this.resolvedCategoryId = null;
            this.resolvedCategoryName = null;
            resolve(null);
          });
      });
    },
    async     handleEnableLocationClick() {
      this.requestLocationAndLoad();
    },

    async isLocationPermissionBlocked() {
      try {
        if (!navigator.permissions?.query) return false;
        const permission = await navigator.permissions.query({
          name: "geolocation",
        });
        return permission.state === "denied";
      } catch (error) {
        return false;
      }
    },

    tryRevokeLocationPermission() {
      try {
        if (!navigator.permissions?.query) return;
        navigator.permissions.query({ name: "geolocation" }).then((p) => {
          if (typeof p.revoke === "function") p.revoke();
        });
      } catch (e) {
        /* ignore */
      }
    },

    requestLocationAndLoad() {
      try {
        this.tryRevokeLocationPermission();
        this.locationMessage = "Requesting location permission...";
        this.loading = true;
        if (typeof window === "undefined") {
          this.loading = false;
          return;
        }
        if (!window.isSecureContext && window.location.hostname !== "localhost") {
          this.locationRequired = true;
          this.locationMessage =
            "Location requires a secure connection (HTTPS). Please open this site on HTTPS and retry.";
          this.loading = false;
          return;
        }
        if (!navigator.geolocation) {
          const fallback = this.getKochiFallbackLocation();
          this.locationRequired = false;
          this.locationMessage =
            "Please allow location access to view available services in this category.";
          this.loading = true;
          this.resolveCategoryId().then(async () => {
            if (!this.resolvedCategoryId) {
              this.services = [];
              this.loading = false;
              return;
            }
            await this.loadServices(fallback);
          });
          return;
        }

        navigator.geolocation.getCurrentPosition(
        async (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            allowed: true,
            error: null,
          };
          localStorage.setItem("userLocation", JSON.stringify(location));
          sessionStorage.setItem("userLocation", JSON.stringify(location));
          this.locationRequired = false;
          this.locationMessage =
            "Please allow location access to view available services in this category.";
          await this.resolveCategoryId();
          if (!this.resolvedCategoryId) {
            this.services = [];
            this.loading = false;
            return;
          }
          await this.loadServices(location);
        },
        () => {
          const fallback = this.getKochiFallbackLocation();
          this.locationRequired = false;
          this.locationMessage =
            "Using Kochi location to continue (location blocked/denied).";
          this.resolveCategoryId().then(async () => {
            if (!this.resolvedCategoryId) {
              this.services = [];
              this.loading = false;
              return;
            }
            await this.loadServices(fallback);
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 0,
        }
      );
      } catch (err) {
        console.error("Location request error:", err);
        const fallback = this.getKochiFallbackLocation();
        this.locationRequired = false;
        this.locationMessage = "Using Kochi location to continue.";
        this.resolveCategoryId().then(async () => {
          if (!this.resolvedCategoryId) {
            this.services = [];
            this.loading = false;
            return;
          }
          await this.loadServices(fallback);
        });
      }
    },

    async loadServices(location) {
      const categoryId = this.resolvedCategoryId;
      if (!categoryId) {
        this.services = [];
        this.loading = false;
        return;
      }
      try {
        let response;
        try {
          response = await fetchServicesByCategory({
            categoryId,
            page: 1,
            pageSize: 50,
            lat: location.latitude,
            lng: location.longitude,
          });
        } catch (locationError) {
          if (locationError.response?.status !== 500) throw locationError;
          response = await fetchServicesByCategory({
            categoryId,
            page: 1,
            pageSize: 50,
          });
        }

        this.services = (response?.data?.data?.services || []).map(
          enrichAcServiceContent,
        );
      } catch (error) {
        console.error("Failed to load services by category", error);
        this.services = [];
      } finally {
        this.loading = false;
      }
    },

    openService(service) {
      const isUSRoute =
        this.$route.meta?.location === "us" ||
        this.$route.path.startsWith("/us");
      const enrichedService = enrichAcServiceContent(service);
      sessionStorage.setItem("selectedService", JSON.stringify(enrichedService));
      const slug = slugifyForUrl(service.title || "");
      sessionStorage.setItem(`service_slug_${slug}`, service._id);
      this.$router.push({
        name: isUSRoute ? "ServiceDetailsUS" : "ServiceDetails",
        params: { serviceSlug: slug },
      });
    },
  },
};
</script>

<style scoped>
.category-services-page {
  min-height: 100vh;
  background: #f8fafc;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border-radius: 14px;
  padding: 12px 14px;
  border: 1px solid #f0f2f5;
}

.back-btn {
  background: #fff3ed;
  border-radius: 10px;
}

.eyebrow {
  font-size: 12px;
  letter-spacing: 1px;
  text-transform: uppercase;
  font-weight: 700;
  color: #f46523;
}

.title {
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  color: #111827;
}

.service-card {
  border-radius: 12px;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.service-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.12);
}

.service-name {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

.loading-wrap {
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 600px) {
  .title {
    font-size: 22px;
  }
}

.location-how-to-box {
  text-align: left;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 16px 20px;
  margin: 24px auto 0;
  max-width: 520px;
}
.location-how-to-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
}
.location-how-to-steps { font-size: 13px; color: #4b5563; line-height: 1.6; }
.location-how-to-steps ol { margin: 0 0 0 18px; padding: 0; }
.location-how-to-steps li { margin-bottom: 6px; }
.location-how-to-browser { font-size: 13px; color: #374151; }
.location-guide-links { font-size: 13px; padding-top: 8px; border-top: 1px solid #e5e7eb; }
.location-guide-links-label { color: #6b7280; margin-right: 8px; }
.location-guide-link { color: #f46523; font-weight: 600; text-decoration: none; }
.location-guide-link:hover { text-decoration: underline; }
.location-guide-sep { color: #9ca3af; margin: 0 6px; }
.location-tip {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
}
</style>
